import React, { useEffect, useMemo, useState } from 'react';

import { useInfiniteQueryStoreFilterHook } from '@fd/customHooks/useInfiniteQuerySoreFilter';
import { App, StoreHeader, Teammate, TeammateBase } from '@flipdish/api-client-typescript';
import { Property } from '@flipdish/orgmanagement';
import Email from '@mui/icons-material/Email';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Hidden from '@mui/material/Hidden';
import InputAdornment from '@mui/material/InputAdornment';
import Modal from '@mui/material/Modal';
import { type Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import withStyles from '@mui/styles/withStyles';
import clsx from 'clsx';
import debounce from 'lodash/debounce';
import { getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';
import Permissions from 'react-redux-permissions';
import { Link, withRouter } from 'react-router-dom';
import { compose } from 'recompose';

import { getSalesChannelType } from '@fd/ui/utils/generateSalesChannelsTypes';

import {
  deleteTeammate,
  getTeammateDetails,
  getTeammates as getTeammatesFromApi,
  grantTeammateAccess,
  updateTeammateDetails,
} from '../../actions/teammates.actions';
import { teammatesConstants } from '../../constants/teammates.constants';
import { validateEmail } from '../../helpers/utilities';
import { notify, NotifyProps } from '../../layouts/Notify/actions';
import { flagService } from '../../services';
import PageLayout from '../../ui/Layout';
import PaperContainer from '../../ui/Layout/PaperContainer';
import { LoadingButton } from '../../ui/LoadingButton';
import { OptionType } from '../../ui/Select/Select';
import { getStoresByAppId } from '../Order/services';
import useStoreToSalesChannelTransformerWithQuery from '../Settings/utils/useStoreToSalesChannelTransformerWithQuery';
import {
  ALL_PROPERTIES_KEY,
  ALL_STORES_KEY,
  getAllPropertiesOptionItem,
  getAllStoresOptionItem,
} from './constants';
import { ModifyPermissionLevel } from './ModifyPermissionLevel';
import { getCurrentUser, getTeammate, getTeammatesLoading } from './selectors';

const styles = (theme: Theme) => ({
  permissionsText: {
    fontSize: '1.125rem',
    fontWeight: 'normal' as any,
    fontStyle: 'normal',
    fontStretch: 'normal',
    lineHeight: 'normal',
    letterSpacing: '0.2px',
    color: 'rgba(0, 0, 0, 0.87)',
  },
  responsiveTextField: {
    fontSize: '16px',
    fontWeight: 'normal' as any,
    fontStyle: 'normal',
    fontStretch: 'normal',
    lineHeight: '1.5',
    letterSpacing: '0.2px',
    marginBottom: theme.spacing(3),
    color: 'rgba(0, 0, 0, 0.87)',
  },
  label: {
    left: '42px',
    fontWeight: 'normal' as any,
    fontStyle: 'normal',
    fontStretch: 'normal',
    lineHeight: '1.33',
    letterSpacing: '0.4px',
    color: 'rgba(0, 0, 0, 0.6)',
  },
  responsiveLabel: {
    fontWeight: 'normal' as any,
    fontStyle: 'normal',
    fontStretch: 'normal',
    lineHeight: '1.33',
    letterSpacing: '0.4px',
    color: 'rgba(0, 0, 0, 0.87)',
  },
  inputAdornment: {
    marginTop: '0!important',
    marginRight: '16px',
    paddingLeft: '4px',
    color: 'rgba(0, 0, 0, 0.54)',
  },
  responsiveAdornment: {
    color: 'rgba(0, 0, 0, 0.54)',
  },
  buttons: {
    paddingLeft: '56px',
    justifyContent: 'flex-end',
    paddingTop: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: '20px',
    [theme.breakpoints.down(350)]: {
      paddingLeft: theme.spacing(2),
    },
  },
  cancel: {
    marginRight: theme.spacing(1),
  },
  remove: {
    marginRight: 'auto',
  },
  hyperlink: {
    textDecoration: 'none',
  },
  modalPaper: {
    position: 'absolute' as any,
    width: '280px',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    top: '50%',
    left: '50%',
    transform: 'translate(-50%,-50%)',
    borderRadius: '4px',
  },
  buttonsHolder: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '10px',
  },
  button: {
    fontSize: '14px',
    fontWeight: '500' as any,
    fontStyle: 'normal',
    fontStretch: 'normal',
    lineHeight: '1.4',
    letterSpacing: '1.3px',
    marginRight: theme.spacing(2),
  },
  modalHeading: {
    fontSize: '20px',
    color: 'rgba(0,0,0,0.87)',
    fontWeight: '500' as any,
    fontStyle: 'normal',
    fontStretch: 'normal',
    lineHeight: 'normal',
    letterSpacing: '0.3px',
    height: '24px',
    marginBottom: '12px',
  },
  modalSubtitle: {
    fontSize: '16px',
    color: 'rgba(0,0,0,0.6)',
    fontWeight: 'normal' as any,
    fontStyle: 'normal',
    fontStretch: 'normal',
    lineHeight: '1.75',
    letterSpacing: '0.5px',
    marginBottom: '34px',
  },
  modalButton: {
    fontSize: '14px',
    fontWeight: '500' as any,
    fontStyle: 'normal',
    fontStretch: 'normal',
    lineHeight: '1.14',
    letterSpacing: '1.3px',
  },
});

interface ITeammatesModifyProps {
  classes: any;
  currentApp: App;
  history: any;
  match: any;
  teammate: Teammate;
  teammateLoading: boolean;
  teammates: Teammate[];
  translate: any;
  currentUser: Teammate;
}

interface IExternalProps {
  showSuccessSnackbar?: (message: string) => any;
  stores: StoreHeader[];
  properties: Property[];
  isPropertiesLoading: boolean;
}

type Props = ITeammatesModifyProps &
  IExternalProps &
  MappedDispatch & {
    showPropertyManagementPermissions?: boolean;
    hideStoreManagementPermissions?: boolean;
  };

type StoreID = number;
// string for all stores id as this prevent us ever hitting the store id
type SelectedStores = Array<{ label: string; value: StoreID | string }> | null;

type SelectedProperties = Array<{ label: string; value: string }> | null;

const filterValuesBasedOnLastSelection = (stores: any[]) => {
  //multiple selected and last is All stores
  if (stores.length > 1 && stores[stores.length - 1]?.value === ALL_STORES_KEY) {
    return stores.filter((store) => store?.value === ALL_STORES_KEY);
    //multiple selected and first is all stores
  } else if (stores.length > 1 && stores.some((store) => store?.value === ALL_STORES_KEY)) {
    return stores.filter((storeValue) => storeValue?.value !== ALL_STORES_KEY);
  } else {
    return stores;
  }
};

const filterPropertiesBasedOnLastSelection = (properties: any[]) => {
  //multiple selected and last is All properties
  if (properties.length > 1 && properties[properties.length - 1]?.value === ALL_PROPERTIES_KEY) {
    return properties.filter((property) => property?.value === ALL_PROPERTIES_KEY);
    //multiple selected and first is all properties
  } else if (
    properties.length > 1 &&
    properties.some((property) => property?.value === ALL_PROPERTIES_KEY)
  ) {
    return properties.filter((propertyValue) => propertyValue?.value !== ALL_PROPERTIES_KEY);
  } else {
    return properties;
  }
};

const Modify: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const {
    classes,
    currentApp,
    currentUser,
    match,
    notify,
    stores,
    properties,
    isPropertiesLoading,
    teammate,
    teammateLoading,
    translate,
    updateTeammateDetails,
    dispatchGetTeammatesFromApi,
    grantTeammateAccess,
    showPropertyManagementPermissions: showPropertyLevelPermissions,
    hideStoreManagementPermissions: hideStoreLevelPermissions,
  } = props;

  const storeIdAssociations = useStoreToSalesChannelTransformerWithQuery();
  const initialSelectedStoresWithAllStoresOption = [
    getAllStoresOptionItem(translate('All_sales_channels')),
  ];

  const initialSelectedPropertiesWithAllPropertiesOption = [
    getAllPropertiesOptionItem(translate('All_Properties')),
  ];

  const [query, setQuery] = useState('');
  const [selectedValue, setSelectedValue] = useState<TeammateBase.AppAccessLevelEnum | null>(null);
  const [open, setOpen] = useState<boolean>(false);

  const [storeManagerStores, setStoreManagerStores] = useState<SelectedStores>(
    initialSelectedStoresWithAllStoresOption
  );
  const [storeOwnerStores, setStoreOwnerStores] = useState<SelectedStores>(
    initialSelectedStoresWithAllStoresOption
  );
  const [storeStaffStores, setStoreStaffStores] = useState<SelectedStores>(
    initialSelectedStoresWithAllStoresOption
  );
  const [financeManagerStores, setFinanceManagerStores] = useState<SelectedStores>(
    initialSelectedStoresWithAllStoresOption
  );
  const [storeReadOnlyAccessStores, setStoreReadOnlyAccessStores] = useState<SelectedStores>(
    initialSelectedStoresWithAllStoresOption
  );
  const [integratorStores, setIntegratorStores] = useState<SelectedStores>(
    initialSelectedStoresWithAllStoresOption
  );
  const [propertyManagerProperties, setPropertyManagerProperties] = useState<SelectedProperties>(
    initialSelectedPropertiesWithAllPropertiesOption
  );
  const [propertyOwnerProperties, setPropertyOwnerProperties] = useState<SelectedProperties>(
    initialSelectedPropertiesWithAllPropertiesOption
  );

  const [newEmail, setNewEmail] = useState<string>('');
  const [setValueInitial, setSetValueInitial] = useState<boolean>(false);
  const [storeOptions, setStoreOptions] = useState<OptionType[]>();
  const [propertyOptions, setPropertyOptions] = useState<OptionType[]>();

  const {
    FinanceManger,
    Owner,
    ManagedOwner,
    StoreOwner,
    StoreManager,
    StoreStaff,
    StoreReadOnlyAccess,
    Integrator,
    PropertyManager,
    PropertyOwner,
  } = TeammateBase.AppAccessLevelEnum;

  // #region useInfiniteQuery
  const fetchDataProps = {
    appId: currentApp.AppId!,
    query,
  };

  const {
    data: getStores,
    hasNextPage: hasStores,
    fetchNextPage: fetchStores,
    isFetchingNextPage,
    isPending,
  } = useInfiniteQueryStoreFilterHook(fetchDataProps, getStoresByAppId, 'loadByAppId');

  useEffect(() => {
    if (getStores && getStores.pages) {
      const addNewStoreOptions = getStores.pages.reduce<OptionType[]>((total, page) => {
        page.Data.forEach((store) => {
          const association = storeIdAssociations.find(
            (association) => association.storeId === store?.StoreId
          );
          total.push({
            value: Number(store.StoreId),
            label: `${store.Name}${getSalesChannelType(association?.salesChannelType ?? '')}`,
          });
        });
        return total;
      }, []);
      setStoreOptions(addNewStoreOptions);
    }
  }, [getStores?.pages, storeIdAssociations]);

  const handleScroll = ({ target }) => {
    const { scrollHeight, clientHeight, scrollTop } = target;

    const hasScrollBar = scrollHeight > clientHeight;
    const scrollTopMax = scrollHeight - clientHeight;

    if (hasScrollBar && hasStores && !isFetchingNextPage && Math.ceil(scrollTop) >= scrollTopMax) {
      fetchStores();
    }
  };

  const onSearchQueryChange = useMemo(
    () =>
      debounce((query: string) => {
        setQuery(query);
      }, 500),
    []
  );
  //#endregion

  const getSelectedStores = (): SelectedStores => {
    let selectedStores: SelectedStores = [];

    if (props.teammate.StoreIds && stores) {
      selectedStores = [];

      for (const StoreId of props.teammate.StoreIds) {
        const newStore = stores.find((store) => {
          return store.StoreId === StoreId;
        });
        const association = storeIdAssociations.find(
          (association) => association.storeId === newStore?.StoreId
        );
        if (newStore) {
          selectedStores!.push({
            label: `${newStore.Name}${getSalesChannelType(association?.salesChannelType ?? '')}`,
            value: Number(newStore.StoreId),
          });
        }
      }
    }

    if (teammate?.HasAccessToAllStores && selectedStores?.length === 0) {
      selectedStores.push(initialSelectedStoresWithAllStoresOption[0]);
    }
    return selectedStores;
  };

  useEffect(() => {
    if (properties) {
      const propertyOptionsList = properties.map((property) => ({
        value: property.propertyId!,
        label: property.name!,
      }));
      setPropertyOptions(propertyOptionsList);
    }
  }, [properties]);

  const getSelectedProperties = (): SelectedProperties => {
    let selectedProperties: SelectedProperties = [];
    if (props.teammate.PropertyIds && properties) {
      selectedProperties = [];

      for (const PropertyId of props.teammate.PropertyIds) {
        const newProperty = properties.find((property) => {
          return property.propertyId === PropertyId;
        });
        if (newProperty) {
          selectedProperties.push({
            label: newProperty.name!,
            value: newProperty.propertyId!,
          });
        }
      }
    }

    if (teammate?.HasAccessToAllStores && selectedProperties?.length === 0) {
      selectedProperties.push(initialSelectedPropertiesWithAllPropertiesOption[0]);
    }

    return selectedProperties;
  };

  useEffect(() => {
    if (match.params.teammateId && teammate && !setValueInitial) {
      if (stores && stores.length > 0) {
        handleCleanupStoresAndProperties();
        switch (teammate.AppAccessLevel) {
          case TeammateBase.AppAccessLevelEnum.Owner:
            setSelectedValue(Owner);
            setSetValueInitial(true);
            break;
          case TeammateBase.AppAccessLevelEnum.ManagedOwner:
            setSelectedValue(ManagedOwner);
            setSetValueInitial(true);
            break;
          case TeammateBase.AppAccessLevelEnum.StoreOwner:
            setSelectedValue(StoreOwner);
            setSetValueInitial(true);
            setStoreOwnerStores(getSelectedStores());
            break;
          case TeammateBase.AppAccessLevelEnum.StoreManager:
            setSelectedValue(StoreManager);
            setSetValueInitial(true);
            setStoreManagerStores(getSelectedStores());
            break;
          case TeammateBase.AppAccessLevelEnum.StoreStaff:
            setSelectedValue(StoreStaff);
            setSetValueInitial(true);
            setStoreStaffStores(getSelectedStores());
            break;
          case TeammateBase.AppAccessLevelEnum.FinanceManger:
            setSelectedValue(FinanceManger);
            setSetValueInitial(true);
            setFinanceManagerStores(getSelectedStores());
            break;
          case TeammateBase.AppAccessLevelEnum.StoreReadOnlyAccess:
            setSelectedValue(StoreReadOnlyAccess);
            setSetValueInitial(true);
            setStoreReadOnlyAccessStores(getSelectedStores());
            break;
          case TeammateBase.AppAccessLevelEnum.Integrator:
            setSelectedValue(Integrator);
            setSetValueInitial(true);
            setIntegratorStores(getSelectedStores());
            break;
        }
      }

      if (properties && properties.length > 0) {
        switch (teammate.AppAccessLevel) {
          case TeammateBase.AppAccessLevelEnum.PropertyManager:
            handleCleanupStoresAndProperties();
            setSelectedValue(PropertyManager);
            setSetValueInitial(true);
            setPropertyManagerProperties(getSelectedProperties());
            break;
          case TeammateBase.AppAccessLevelEnum.PropertyOwner:
            handleCleanupStoresAndProperties();
            setSelectedValue(PropertyOwner);
            setSetValueInitial(true);
            setPropertyOwnerProperties(getSelectedProperties());
            break;
        }
      }
    }
  }, [teammate, stores, properties]);

  const handleStoresChange = (name) => (value) => {
    const filteredValue = filterValuesBasedOnLastSelection(value);
    switch (name) {
      case FinanceManger:
        setFinanceManagerStores(filteredValue);
        break;
      case StoreManager:
        setStoreManagerStores(filteredValue);
        break;
      case StoreOwner:
        setStoreOwnerStores(filteredValue);
        break;
      case StoreStaff:
        setStoreStaffStores(filteredValue);
        break;
      case StoreReadOnlyAccess:
        setStoreReadOnlyAccessStores(filteredValue);
        break;
      case Integrator:
        setIntegratorStores(filteredValue);
        break;
    }
  };

  const handlePropertiesChange = (name) => (value) => {
    const filteredValue = filterPropertiesBasedOnLastSelection(value);

    if (name === PropertyManager) {
      setPropertyManagerProperties(filteredValue);
    } else if (name === PropertyOwner) {
      setPropertyOwnerProperties(filteredValue);
    }
  };

  const handleCleanupStoresAndProperties = () => {
    setFinanceManagerStores(null);
    setStoreManagerStores(null);
    setStoreOwnerStores(null);
    setStoreStaffStores(null);
    setStoreReadOnlyAccessStores(null);
    setIntegratorStores(null);
    setPropertyManagerProperties(null);
    setPropertyOwnerProperties(null);
  };

  const handleNewEmailChange = (event) => {
    setNewEmail(event.target.value);
  };

  const handlePermissionChange = (value) => {
    setSelectedValue(value);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const deleteTeammate = () => {
    const { history, match } = props;
    handleClose();

    const callback = () => {
      history.push(`/${currentApp.AppId}/teammates/`);
    };

    props.deleteTeammate(match.params.teammateId, callback);
  };

  const getStoreIds = () => {
    const allStoreIds = stores?.map((store) => store.StoreId);

    const {
      Owner,
      ManagedOwner,
      StoreOwner,
      StoreManager,
      StoreStaff,
      FinanceManger,
      StoreReadOnlyAccess,
      Integrator,
    } = TeammateBase.AppAccessLevelEnum;

    const hasAccessToAllStores =
      currentUser.AppAccessLevel &&
      [Owner, ManagedOwner, StoreOwner].includes(currentUser.AppAccessLevel) &&
      currentUser.HasAccessToAllStores;

    const getAllStoreIds = () => {
      if (hasAccessToAllStores) {
        return [];
      }
      return allStoreIds;
    };

    const getIds = (stores: SelectedStores) => {
      return (stores?.length ?? 0) > 0 ? stores?.map((store) => store.value) : getAllStoreIds();
    };

    switch (selectedValue) {
      case Owner:
      case ManagedOwner:
        return initialSelectedStoresWithAllStoresOption;
      case StoreOwner:
        return getIds(storeOwnerStores);
      case StoreManager:
        return getIds(storeManagerStores);
      case StoreStaff:
        return getIds(storeStaffStores);
      case FinanceManger:
        return getIds(financeManagerStores);
      case StoreReadOnlyAccess:
        return getIds(storeReadOnlyAccessStores);
      case Integrator:
        return getIds(integratorStores);
    }
  };

  const validateForm = (
    storeIds: ReturnType<typeof getStoreIds>,
    propertyIds: string[],
    teammates: Teammate[],
    isNewUser: boolean
  ) => {
    const isStoresRequired = ![Owner, ManagedOwner, PropertyManager, PropertyOwner].includes(
      selectedValue!
    );
    const isPropertiesRequired = [PropertyManager, PropertyOwner].includes(selectedValue!);

    if (isNewUser && newEmail === '') {
      notify({
        variant: 'error',
        message: translate('Email_cannot_be_empty'),
      });
      return true;
    } else if (isNewUser && !validateEmail(newEmail)) {
      notify({
        variant: 'error',
        message: translate('Incorrect_email_format'),
      });
      return true;
    } else if (!selectedValue) {
      notify({
        variant: 'error',
        message: 'Select a permission',
      });
      return true;
    } else if (isStoresRequired && (!storeIds || storeIds?.length === 0)) {
      notify({
        variant: 'error',
        // need a string translated and added to loco
        message: translate('Select_the_sales_channels_that_the_permission_appl'),
      });
      return true;
    } else if (isPropertiesRequired && (!propertyIds || propertyIds?.length === 0)) {
      notify({
        variant: 'error',
        message: translate('Select_the_properties_that_the_permission_appl'),
      });
      return true;
    } else if (isNewUser) {
      const existingIndex = teammates.findIndex((teammate) => teammate.Email === newEmail);
      if (existingIndex != -1) {
        notify({
          variant: 'error',
          message: translate('You_have_already_invited', {
            email: newEmail,
          }),
        });
        return true;
      }
    }
  };

  const modifyTeammate = () => {
    const { history, match, teammates } = props;
    const storeIds = getStoreIds();
    let propertyIds: string[] = [];
    if (selectedValue === PropertyManager) {
      propertyIds = propertyManagerProperties?.map((property) => property.value) ?? [];
    } else if (selectedValue === PropertyOwner) {
      propertyIds = propertyOwnerProperties?.map((property) => property.value) ?? [];
    }
    const isNewUser = !match.params.teammateId;
    if (validateForm(storeIds, propertyIds, teammates, isNewUser)) {
      return;
    }
    if (isNewUser) {
      const callback = () => {
        history.push(`/${currentApp.AppId}/teammates/`);
      };

      grantTeammateAccess(newEmail, selectedValue, storeIds, propertyIds, callback);
    } else {
      const teammate: Teammate = props.teammate;

      const callback = () => {
        dispatchGetTeammatesFromApi();
        history.push(`/${currentApp.AppId}/teammates/`);
      };

      updateTeammateDetails(
        teammate.TeammateId,
        selectedValue,
        currentUser.HasAccessToAllStores,
        storeIds,
        propertyIds,
        callback
      );
    }
  };

  const appId = currentApp != null ? currentApp.AppId : 'global';

  const isMe = teammate && currentUser.Email === teammate.Email;
  const header = !teammate ? (
    <Grid container>
      <Grid item xs={12} sm={12} md={6}>
        <TextField
          className={classes.responsiveTextField}
          variant="outlined"
          label={translate('Teammates_email_address')}
          InputLabelProps={{ className: classes.responsiveLabel }}
          value={newEmail}
          fullWidth
          placeholder={translate('Email')}
          autoFocus
          onChange={handleNewEmailChange}
          InputProps={{
            inputProps: {
              type: 'email',
              'data-fd': 'teammate-invite-email',
            },
            endAdornment: (
              <InputAdornment position="end" className={classes.responsiveAdornment}>
                <Email />
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h2" component="h2" className={classes.permissionsText}>
          {translate('Access_permissions')}
        </Typography>
      </Grid>
    </Grid>
  ) : null;

  const isNewUser = !match.params.teammateId;

  return (
    <PageLayout
      header={header}
      toParent={`/${currentApp.AppId}/teammates`}
      documentTitle="Teammates"
      caption={
        teammate
          ? teammate.Name && teammate.Name !== teammate.Email
            ? `${teammate.Name}, ${teammate.Email}`
            : teammate.Email
          : ''
      }
      title={match.params.teammateId ? translate('Edit_teammate') : translate('Invite_teammate')}
    >
      <Modal
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        open={open}
        onClose={handleClose}
      >
        <div className={classes.modalPaper}>
          <Typography variant="h6" className={classes.modalHeading}>
            {translate('Remove_user')}?
          </Typography>
          <Typography variant="subtitle1" className={classes.modalSubtitle}>
            {translate('Pressing_remove', { appName: currentApp.Name })}
          </Typography>
          <div className={classes.buttonsHolder}>
            <Button
              color="primary"
              onClick={handleClose}
              className={classes.modalButton}
              data-fd="teammate-modal-cancel"
            >
              {translate('Cancel')}
            </Button>
            <Button
              color="primary"
              onClick={deleteTeammate}
              className={classes.modalButton}
              data-fd="teammate-modal-remove"
            >
              {translate('Remove')}
            </Button>
          </div>
        </div>
      </Modal>
      <PaperContainer fluid>
        <Permissions allowed={[App.AppResourceSetEnum.CreateTeammateOwner]}>
          <ModifyPermissionLevel
            dataFd="owner"
            desc={translate('Can_access_all_properties_and_settings')}
            disabled={isMe}
            handlePermissionChange={handlePermissionChange}
            handleStoresChange={handleStoresChange}
            isNewUser={isNewUser}
            permissionLevel={Owner}
            selectedValue={selectedValue}
            title={translate('Owner')}
          />
        </Permissions>

        <Permissions allowed={[App.AppResourceSetEnum.CreateTeammateManagedOwner]}>
          <ModifyPermissionLevel
            dataFd="managed-owner"
            desc={translate('Managed_owner_access_description')}
            disabled={isMe}
            handlePermissionChange={handlePermissionChange}
            handleStoresChange={handleStoresChange}
            isNewUser={isNewUser}
            permissionLevel={ManagedOwner}
            selectedValue={selectedValue}
            title={translate('Managed_owner')}
          />
        </Permissions>

        {showPropertyLevelPermissions && (
          <>
            <Permissions allowed={[App.AppResourceSetEnum.CreateTeammatePropertyOwner]}>
              <ModifyPermissionLevel
                dataFd="property-owner"
                desc={translate('Property_owner_description')}
                disabled={isMe}
                handlePermissionChange={handlePermissionChange}
                handleScroll={handleScroll}
                handlePropertiesChange={handlePropertiesChange}
                isNewUser={isNewUser}
                isLoading={isPropertiesLoading}
                onSearchQueryChange={onSearchQueryChange}
                permissionLevel={PropertyOwner}
                selectedValue={selectedValue}
                selectedProperties={propertyOwnerProperties}
                propertyOptions={propertyOptions}
                title={translate('Property_owner')}
              />
            </Permissions>

            <Permissions allowed={[App.AppResourceSetEnum.CreateTeammatePropertyManager]}>
              <ModifyPermissionLevel
                dataFd="property-manager"
                desc={translate('Property_manager_description')}
                disabled={isMe}
                handlePermissionChange={handlePermissionChange}
                handleScroll={handleScroll}
                handlePropertiesChange={handlePropertiesChange}
                isNewUser={isNewUser}
                isLoading={isPropertiesLoading}
                onSearchQueryChange={onSearchQueryChange}
                permissionLevel={PropertyManager}
                selectedValue={selectedValue}
                selectedProperties={propertyManagerProperties}
                propertyOptions={propertyOptions}
                title={translate('Property_manager')}
              />
            </Permissions>
          </>
        )}

        {!hideStoreLevelPermissions && (
          <>
            <Permissions allowed={[App.AppResourceSetEnum.CreateTeammateStoreOwner]}>
              <ModifyPermissionLevel
                dataFd="store-owner"
                desc={translate('Sales_channel_owner_access_description')}
                disabled={isMe}
                handlePermissionChange={handlePermissionChange}
                handleScroll={handleScroll}
                handleStoresChange={handleStoresChange}
                isNewUser={isNewUser}
                isLoading={isPending || isFetchingNextPage}
                onSearchQueryChange={onSearchQueryChange}
                permissionLevel={StoreOwner}
                selectedValue={selectedValue}
                stores={storeOwnerStores}
                suggestions={storeOptions}
                title={translate('Sales_channel_owner')}
              />
            </Permissions>

            <Permissions allowed={[App.AppResourceSetEnum.CreateTeammateStoreManager]}>
              <ModifyPermissionLevel
                dataFd="store-manager"
                desc={translate('Can_access_limited_sales_channel_settings')}
                disabled={isMe}
                handlePermissionChange={handlePermissionChange}
                handleScroll={handleScroll}
                handleStoresChange={handleStoresChange}
                isNewUser={isNewUser}
                isLoading={isPending || isFetchingNextPage}
                onSearchQueryChange={onSearchQueryChange}
                permissionLevel={StoreManager}
                selectedValue={selectedValue}
                stores={storeManagerStores}
                suggestions={storeOptions}
                title={translate('Sales_channel_manager')}
              />
            </Permissions>
          </>
        )}

        <Permissions allowed={[App.AppResourceSetEnum.CreateTeammateStoreStaff]}>
          <ModifyPermissionLevel
            dataFd="store-staff"
            desc={translate('Can_access_limited_information')}
            disabled={isMe}
            handlePermissionChange={handlePermissionChange}
            handleScroll={handleScroll}
            handleStoresChange={handleStoresChange}
            isNewUser={isNewUser}
            isLoading={isPending || isFetchingNextPage}
            onSearchQueryChange={onSearchQueryChange}
            permissionLevel={StoreStaff}
            secondaryDesc={translate('Can_view_all_data')}
            selectedValue={selectedValue}
            stores={storeStaffStores}
            suggestions={storeOptions}
            title={translate('Sales_channel_staff')}
          />
        </Permissions>

        <Permissions allowed={[App.AppResourceSetEnum.CreateTeammateFinanceManager]}>
          <ModifyPermissionLevel
            dataFd="finance-manager"
            desc={translate('Can_view_all_data')}
            disabled={isMe}
            handlePermissionChange={handlePermissionChange}
            handleScroll={handleScroll}
            handleStoresChange={handleStoresChange}
            isNewUser={isNewUser}
            isLoading={isPending || isFetchingNextPage}
            onSearchQueryChange={onSearchQueryChange}
            permissionLevel={FinanceManger}
            selectedValue={selectedValue}
            stores={financeManagerStores}
            suggestions={storeOptions}
            title={translate('Finance_manager')}
          />
        </Permissions>

        <Permissions allowed={[App.AppResourceSetEnum.CreateTeammateStoreReadAccess]}>
          <ModifyPermissionLevel
            dataFd="store-readonly-access"
            desc={translate('Sales_channel_readonly_access_description')}
            disabled={isMe}
            handlePermissionChange={handlePermissionChange}
            handleScroll={handleScroll}
            handleStoresChange={handleStoresChange}
            isNewUser={isNewUser}
            isLoading={isPending || isFetchingNextPage}
            onSearchQueryChange={onSearchQueryChange}
            permissionLevel={StoreReadOnlyAccess}
            selectedValue={selectedValue}
            stores={storeReadOnlyAccessStores}
            suggestions={storeOptions}
            title={translate('Sales_channel_readonly_access')}
          />
        </Permissions>

        <Permissions allowed={[App.AppResourceSetEnum.CreateTeammateIntegrator]}>
          <ModifyPermissionLevel
            dataFd="integrator"
            desc={translate('Integrator_description')}
            disabled={isMe}
            handlePermissionChange={handlePermissionChange}
            handleScroll={handleScroll}
            handleStoresChange={handleStoresChange}
            isNewUser={isNewUser}
            isLoading={isPending || isFetchingNextPage}
            onSearchQueryChange={onSearchQueryChange}
            permissionLevel={Integrator}
            selectedValue={selectedValue}
            stores={integratorStores}
            suggestions={storeOptions}
            title={translate('Integrator')}
          />
        </Permissions>

        <Grid container className={classes.buttons}>
          {match.params.teammateId ? (
            <div className={classes.remove}>
              <Hidden mdDown>
                <Button
                  color="secondary"
                  className={classes.button}
                  onClick={handleOpen}
                  data-fd="teammate-remove"
                  disabled={teammateLoading || isMe}
                >
                  {translate('Remove_user')}
                </Button>
              </Hidden>
              <Hidden mdUp>
                <Button
                  color="secondary"
                  className={classes.button}
                  onClick={handleOpen}
                  data-fd="teammate-remove"
                  disabled={teammateLoading || isMe}
                >
                  {translate('Remove')}
                </Button>
              </Hidden>
            </div>
          ) : null}
          <Link
            to={'/' + appId + '/teammates'}
            className={classes.hyperlink}
            data-fd="teammate-cancel"
          >
            <Button color="primary" className={clsx(classes.button, classes.cancel)}>
              {translate('Cancel')}
            </Button>
          </Link>

          <LoadingButton
            buttonProps={{
              color: 'primary',
              onClick: modifyTeammate,
              'data-fd': 'teammate-save',
              className: classes.button,
              variant: 'contained',
            }}
            wrapperClassName={classes.button}
            loadingResources={[teammatesConstants.SET_TEAMMATE_LOADING]}
            loading={teammateLoading}
            disabled={teammateLoading || isPending || isMe}
          >
            {match.params.teammateId ? translate('Save') : translate('Send_invite')}
          </LoadingButton>
        </Grid>
      </PaperContainer>
    </PageLayout>
  );
};

const mapStateToProps = (state: AppState, props: Props) => {
  const { locale } = state;

  return {
    currentApp: state.currentApp,
    currentUser: getCurrentUser(state),
    teammate: getTeammate(state, props),
    teammateLoading: getTeammatesLoading(state),
    teammates: state.teammates?.teammates || [],
    translate: getTranslate(locale),
    showPropertyManagementPermissions: flagService.isFlagOn(
      state,
      'showPropertyManagementPermissions'
    ),
    hideStoreManagementPermissions: flagService.isFlagOn(state, 'hideStoreManagementPermissions'),
  };
};

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
const mapDispatchToProps = (dispatch: ThunkDispatch) => {
  return {
    dispatchGetTeammatesFromApi: () => {
      dispatch(getTeammatesFromApi());
    },
    getTeammateDetails: (teammateId) => {
      dispatch(getTeammateDetails(teammateId));
    },
    notify: (data: NotifyProps) => {
      dispatch(notify(data));
    },
    updateTeammateDetails: (
      TeammateId,
      selectedValue,
      hasAccessToAllStores,
      storeIds,
      propertyIds,
      callback
    ) => {
      dispatch(
        updateTeammateDetails(
          TeammateId,
          selectedValue,
          hasAccessToAllStores,
          storeIds,
          propertyIds,
          callback
        )
      );
    },
    deleteTeammate: (teammateId, callback) => {
      dispatch(deleteTeammate(teammateId, callback));
    },
    grantTeammateAccess: (email, appAccessLevel, storeIds, propertyIds, callback) => {
      dispatch(grantTeammateAccess(email, appAccessLevel, storeIds, propertyIds, callback));
    },
  };
};

const EnhancedComponent = compose<any, IExternalProps>(
  withRouter,
  withStyles(styles, {
    name: 'Modify',
    withTheme: true,
  }),
  connect(mapStateToProps, mapDispatchToProps)
)(Modify);

const ComponentForTest = compose(
  withStyles(styles, {
    name: 'Modify',
    withTheme: true,
  }),
  connect(mapStateToProps)
)(Modify);

export { EnhancedComponent as Modify, ComponentForTest as ModifyTest };
